import { countryCodeSchema, isoDateString } from '@clockwise/shared';
import { z } from 'zod';

export const HolidaySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  /** ISO 3166-1 alpha-2, uppercase. Display names come from
   *  `countryName()` — they are never stored. */
  country: z.string(),
  /** Plain `YYYY-MM-DD` — a calendar date with no zone implied. Never pass
   *  this through `new Date(...)` for display: that parses as UTC midnight
   *  and shows the previous day west of Greenwich. */
  date: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Holiday = z.infer<typeof HolidaySchema>;

const HolidayBody = z.object({
  // Accepts a code, name, or alias and normalizes to the alpha-2 code —
  // matching the server validator exactly.
  country: countryCodeSchema,
  date: isoDateString(),
  name: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  active: z.boolean().optional(),
});

export const CreateHolidayInput = HolidayBody;
export type CreateHolidayInput = z.infer<typeof CreateHolidayInput>;

export const PatchHolidayInput = HolidayBody.partial();
export type PatchHolidayInput = z.infer<typeof PatchHolidayInput>;

export interface ListHolidaysQuery {
  q?: string;
  country?: string;
  /** Inclusive lower bound, `YYYY-MM-DD`. */
  from?: string;
  /** Inclusive upper bound, `YYYY-MM-DD`. */
  to?: string;
  active?: boolean;
  sortField?: 'date' | 'country' | 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** The list filters the export honours — it mirrors the list page so users
 *  export exactly what they're looking at, free-text search included. */
export type ExportHolidaysQuery = Pick<
  ListHolidaysQuery,
  'q' | 'country' | 'from' | 'to' | 'active'
>;

export type ImportHolidayRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportHolidaysRow {
  rowNumber: number;
  /** Echo of the row's natural key, so a failure report points back at the
   *  offending line. Null when the cell was blank in the source file. */
  country: string | null;
  date: string | null;
  name: string | null;
  outcome: ImportHolidayRowOutcome;
  errors?: string[];
}

export interface ImportHolidaysResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportHolidaysRow[];
}
